/**
 * Moving a tab from one WeaveIn window to another.
 *
 * Every tab host (the main window's strip, each tabbed pop-out window) has a
 * WINDOW ID: its Tauri label on the desktop app (`main`, `app-<n>`,
 * `messaging-<n>`), and on the web a random id per document (pop-outs reuse
 * their host-registry id). Hosts exchange three messages:
 *
 *   weavein:tab-move           source → destination  "take this tab" (menu "Move to ▸ …")
 *   weavein:tab-moved          destination → source  ack: "I have it — let it go"
 *   weavein:tab-move-rejected  destination → source  "no room" (the main window's cap)
 *
 * A drag between windows uses the same ack: the dragged tab carries
 * {tabKey, sourceWindowId, nonce} in a custom MIME type (lib/tabStrip.ts), the
 * window it is dropped on inserts it and sends weavein:tab-moved to the source.
 *
 * NEVER LOSE, NEVER DUPLICATE. The source keeps the tab until an ack for the
 * exact nonce it issued arrives (PendingMoves: each nonce is honoured once, and
 * only within its time limit). A destination takes each nonce once
 * (SeenNonces), so a repeated message cannot add a second copy. If the source
 * window closed mid-drag the destination still has the tab; if the destination
 * never answers, the source keeps it and says so.
 *
 * TRANSPORT. Web: a BroadcastChannel (same-origin windows of this browser
 * profile only). Desktop: Tauri `emitTo(label)`, which only reaches our own
 * app's windows. Every message is shape-checked on receipt, and a window acts
 * only on messages addressed to its own id.
 *
 * A MOVED TAB RELOADS. Each tab is an iframe document; it cannot be carried
 * across windows, so the destination opens a fresh iframe at the page the tab
 * was showing last (its current page key, which follows in-tab navigation),
 * not the one it was first opened at. Unsaved form input in that tab is lost.
 * A destination pop-out that already shows the same page merges the move into
 * that tab instead of opening a duplicate.
 *
 * DESKTOP DRAG BETWEEN WINDOWS. Each Tauri window is its own webview. HTML5
 * drag and drop needs the webview's native file-drop handling switched off
 * (dragDropEnabled: false — src-tauri/tauri.conf.json for `main`, openAppPopout
 * for pop-outs), or WebView2 on Windows swallows every drag. Whether a custom
 * MIME type survives a drag from one webview window to another (WKWebView on
 * macOS, WebView2 on Windows) has NOT been verified on real hardware; when it
 * does not, the drop is simply ignored and the tab stays where it was (dropping
 * outside every window still tears it off into a new one), and "Move to ▸" in
 * the tab's menu is the supported path.
 */
import { config } from '@/src/config';
import {
  APP_WINDOW_LABEL_PREFIX,
  MESSAGING_WINDOW_LABEL_PREFIX,
  parseAppTabKey,
  readHostRegistry,
  HOST_HEARTBEAT_MS,
} from './popout';
import { isValidNonce, isValidWindowId } from './tabStrip';
import { MAIN_WINDOW_LABEL } from './desktopWindow';

export const TAB_MOVE_CHANNEL = 'weavein-tab-move';
/** Desktop event carrying a TabMoveMessage. */
export const TAB_MOVE_EVENT = 'popout://tab-move';
/** How long a menu move waits for the destination's ack before giving up (the tab stays). */
export const MOVE_ACK_TIMEOUT_MS = 5000;
/** How long a dragged tab's nonce stays good (a drag can take a while). */
export const DRAG_NONCE_TTL_MS = 60_000;

export type TabMoveMessage =
  | {
      type: 'weavein:tab-move';
      /** Destination window id. */
      target: string;
      /** Source window id (where the ack goes). */
      source: string;
      nonce: string;
      tabKey: string;
      title?: string;
      /** Insert position in the destination's strip; omitted = at the end. */
      index?: number;
    }
  | { type: 'weavein:tab-moved'; target: string; source: string; nonce: string }
  | { type: 'weavein:tab-move-rejected'; target: string; source: string; nonce: string; reason: 'full' | 'invalid' };

/** A message we could have sent, or null. */
export function parseTabMoveMessage(data: unknown): TabMoveMessage | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  if (!isValidWindowId(d.target) || !isValidWindowId(d.source) || d.target === d.source || !isValidNonce(d.nonce)) return null;
  const base = { target: d.target, source: d.source, nonce: d.nonce };
  switch (d.type) {
    case 'weavein:tab-move': {
      if (!parseAppTabKey(d.tabKey)) return null;
      const msg: TabMoveMessage = { type: d.type, ...base, tabKey: d.tabKey as string };
      if (typeof d.title === 'string' && d.title) msg.title = d.title.slice(0, 120);
      if (d.index !== undefined) {
        if (typeof d.index !== 'number' || !Number.isInteger(d.index) || d.index < 0 || d.index > 64) return null;
        msg.index = d.index;
      }
      return msg;
    }
    case 'weavein:tab-moved':
      return { type: d.type, ...base };
    case 'weavein:tab-move-rejected':
      return d.reason === 'full' || d.reason === 'invalid' ? { type: d.type, ...base, reason: d.reason } : null;
    default:
      return null;
  }
}

export function newNonce(): string {
  try {
    const bytes = new Uint8Array(12);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 24);
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.replace(/[^a-z0-9]/g, '').slice(0, 24).padEnd(8, '0');
  }
}

/** Source side: the moves this window started and has not yet been told the outcome of. */
export class PendingMoves {
  private entries = new Map<string, { uid: string; expires: number }>();

  add(nonce: string, uid: string, ttlMs: number, now: number = Date.now()): void {
    this.prune(now);
    this.entries.set(nonce, { uid, expires: now + ttlMs });
  }

  /** The tab uid for this nonce — once, and only while it is fresh. Unknown or stale → null. */
  take(nonce: string, now: number = Date.now()): string | null {
    const e = this.entries.get(nonce);
    if (!e) return null;
    this.entries.delete(nonce);
    return e.expires >= now ? e.uid : null;
  }

  has(nonce: string): boolean {
    return this.entries.has(nonce);
  }

  private prune(now: number) {
    for (const [n, e] of this.entries) if (e.expires < now) this.entries.delete(n);
  }
}

/** Destination side: nonces already taken, so a repeat never adds a second copy. */
export class SeenNonces {
  private seen: string[] = [];
  constructor(private cap = 64) {}

  /** True the first time a nonce is offered, false after. */
  claim(nonce: string): boolean {
    if (this.seen.includes(nonce)) return false;
    this.seen.push(nonce);
    if (this.seen.length > this.cap) this.seen.shift();
    return true;
  }
}

// ---------------------------------------------------------------- window ids
let webMainId: string | null = null;

/** The main window's id: its label on desktop, a per-document id on the web. */
export function mainWindowId(isDesktop: boolean = config.isDesktop, label: string | null = null): string {
  if (isDesktop) return label ?? MAIN_WINDOW_LABEL;
  if (!webMainId) webMainId = `main-${newNonce().slice(0, 10)}`;
  return webMainId;
}

// ---------------------------------------------------------------- targets
export interface MoveTarget {
  id: string;
  kind: 'main' | 'tabs';
  /** The window's title, or '' for the main window (the menu names it). */
  title: string;
}

function isTabWindowLabel(label: string): boolean {
  return label === MAIN_WINDOW_LABEL || label.startsWith(APP_WINDOW_LABEL_PREFIX) || label.startsWith(MESSAGING_WINDOW_LABEL_PREFIX);
}

/** Strip the " · WeaveIn" suffix a tabbed window puts on its title. */
function shortTitle(title: string): string {
  return title.replace(/\s*·\s*WeaveIn$/, '').trim();
}

/**
 * The other open windows a tab could move to (main window first, then most
 * recently focused). Web: live entries of the host registry. Desktop: the
 * app's main / app-* / messaging-* windows.
 */
export async function listMoveTargets(
  selfId: string,
  opts: { isDesktop?: boolean; now?: number; storage?: Storage } = {},
): Promise<MoveTarget[]> {
  const isDesktop = opts.isDesktop ?? config.isDesktop;
  if (!isDesktop) {
    const now = opts.now ?? Date.now();
    const reg = readHostRegistry(opts.storage);
    return Object.entries(reg)
      .filter(([id, e]) => id !== selfId && now - e.seenAt <= HOST_HEARTBEAT_MS * 3)
      .sort(([, a], [, b]) => (a.kind === 'main' ? 0 : 1) - (b.kind === 'main' ? 0 : 1) || b.focusedAt - a.focusedAt)
      .map(([id, e]) => ({ id, kind: e.kind === 'main' ? 'main' : 'tabs', title: e.kind === 'main' ? '' : shortTitle(e.title ?? '') }));
  }
  try {
    const { getAllWebviewWindows } = await import('@tauri-apps/api/webviewWindow');
    const wins = (await getAllWebviewWindows()).filter((w) => w.label !== selfId && isTabWindowLabel(w.label));
    const out: MoveTarget[] = [];
    for (const w of wins) {
      const kind = w.label === MAIN_WINDOW_LABEL ? 'main' : 'tabs';
      let title = '';
      if (kind === 'tabs') {
        try {
          title = shortTitle(await w.title());
        } catch {
          title = '';
        }
      }
      out.push({ id: w.label, kind, title });
    }
    return out.sort((a, b) => (a.kind === 'main' ? 0 : 1) - (b.kind === 'main' ? 0 : 1));
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------- transport
type Channel = { postMessage: (m: unknown) => void; close?: () => void };

function openChannel(): Channel | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(TAB_MOVE_CHANNEL);
  } catch {
    return null;
  }
}

/** Send one message to another window. Resolves false when there is no way to reach it. */
export async function sendTabMoveMessage(
  msg: TabMoveMessage,
  opts: { isDesktop?: boolean; channel?: Channel | null; focusTarget?: boolean } = {},
): Promise<boolean> {
  const isDesktop = opts.isDesktop ?? config.isDesktop;
  if (!isDesktop) {
    const own = opts.channel === undefined;
    const channel = own ? openChannel() : opts.channel;
    if (!channel) return false;
    try {
      channel.postMessage(msg);
      return true;
    } catch {
      return false;
    } finally {
      if (own) channel.close?.();
    }
  }
  try {
    const { emitTo } = await import('@tauri-apps/api/event');
    await emitTo(msg.target, TAB_MOVE_EVENT, msg);
    if (opts.focusTarget) {
      const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');
      const w = await WebviewWindow.getByLabel(msg.target);
      await w?.unminimize().catch(() => {});
      await w?.setFocus().catch(() => {});
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Listen for messages addressed to `windowId`. Returns an unsubscribe. Only
 * validated messages for this window reach `handler`.
 */
export function subscribeTabMoves(
  windowId: string,
  handler: (msg: TabMoveMessage) => void,
  opts: { isDesktop?: boolean } = {},
): () => void {
  const isDesktop = opts.isDesktop ?? config.isDesktop;
  const accept = (data: unknown) => {
    const msg = parseTabMoveMessage(data);
    if (msg && msg.target === windowId) handler(msg);
  };
  if (!isDesktop) {
    let channel: BroadcastChannel | null = null;
    try {
      channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(TAB_MOVE_CHANNEL);
    } catch {
      channel = null;
    }
    if (!channel) return () => {};
    channel.onmessage = (e: MessageEvent) => accept(e.data);
    return () => channel?.close();
  }
  let unlisten: (() => void) | undefined;
  let cancelled = false;
  import('@tauri-apps/api/webviewWindow')
    .then(({ getCurrentWebviewWindow }) => getCurrentWebviewWindow().listen<unknown>(TAB_MOVE_EVENT, (e) => accept(e.payload)))
    .then((fn) => {
      if (cancelled) fn();
      else unlisten = fn;
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    unlisten?.();
  };
}
